class MainCollectionGrid extends HTMLElement {
    constructor() {
        super();
        this.isLoading = false;
        this.prefetchedPages = new Map();
        this.prefetchIdleHandle = null;
        this.prefetchTimeoutHandle = null;
        this.prefetchController = null;
        this.maxPrefetchCacheSize = 5;
        this.delegate = null;
        this._delegateRetryTimer = null;
        this.boundLoadMoreClick = this.onLoadMoreClick.bind(this);
        this.boundFiltersRerendered = this.onFiltersRerendered.bind(this);
        this.boundOverlayClick = this.onOverlayClick.bind(this);
        this.boundLayoutSwitcherClick = this.onLayoutSwitcherClick.bind(this);
        this.boundPaginationFinished = this.onPaginationFinished.bind(this);
        this.documentEventsBound = false;
        this.promoResizeObserver = null;
        this._promoHeightRaf = null;
        this.layoutKey = 'collectionGridLayout';
        this.validLayouts = ['editorial', 'regular', 'small-regular', 'images-grid', 'image-mobile'];
    }

    connectedCallback() {
        this.setup();
        this.setupLayoutSwitcher();
        this.setupFilterDropdowns();
        this.bindDocumentEvents();
        this.initDelegateBindings();
        this.setupEditorialPromoHeight();
    }

    disconnectedCallback() {
        this.teardownEditorialPromoHeight();
        this.teardownDelegateBindings();
        this.unbindDocumentEvents();
        this.cancelPrefetchSchedule();
        this.prefetchController?.abort();
        this.prefetchController = null;
    }

    initDelegateBindings() {
        if (this.delegate) {
            this.delegate.off('click', '[data-action="load-more"]', this.boundLoadMoreClick);
            this.delegate.on('click', '[data-action="load-more"]', this.boundLoadMoreClick);
            return;
        }

        if (typeof Delegate === 'function') {
            this.delegate = new Delegate(this);
            this.delegate.on('click', '[data-action="load-more"]', this.boundLoadMoreClick);
            return;
        }

        // Global script order can vary; retry until Delegate is available.
        this._delegateRetryTimer = window.setTimeout(() => {
            this._delegateRetryTimer = null;
            if (!this.isConnected) return;
            this.initDelegateBindings();
        }, 50);
    }

    teardownDelegateBindings() {
        if (this._delegateRetryTimer) {
            clearTimeout(this._delegateRetryTimer);
            this._delegateRetryTimer = null;
        }
        if (!this.delegate) return;
        this.delegate.off('click', '[data-action="load-more"]', this.boundLoadMoreClick);
    }

    bindDocumentEvents() {
        if (this.documentEventsBound) return;
        this.documentEventsBound = true;
        document.addEventListener('filters:rerendered', this.boundFiltersRerendered);
        document.addEventListener('click', this.boundOverlayClick);
        document.addEventListener('click', this.boundLayoutSwitcherClick);
        document.addEventListener('pagination:finished', this.boundPaginationFinished);
    }

    unbindDocumentEvents() {
        if (!this.documentEventsBound) return;
        this.documentEventsBound = false;
        document.removeEventListener('filters:rerendered', this.boundFiltersRerendered);
        document.removeEventListener('click', this.boundOverlayClick);
        document.removeEventListener('click', this.boundLayoutSwitcherClick);
        document.removeEventListener('pagination:finished', this.boundPaginationFinished);
    }

    onFiltersRerendered() {
        this.setup();
        this.setupLayoutSwitcher();
        this.setupFilterDropdowns();
        this.prefetchedPages.clear();
        this.scheduleIdlePrefetch();
        this.scheduleEditorialPromoHeightUpdate();
    }

    getLayoutFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const view = params.get('view');
        return view && this.validLayouts.indexOf(view) !== -1 ? view : null;
    }

    applyLayout(layout, grid, buttons) {
        if (!grid) return;
        this.validLayouts.forEach(function (name) {
            grid.classList.remove('collection__main-grid--' + name);
        });
        grid.classList.add('collection__main-grid--' + layout);
        grid.setAttribute('data-layout', layout);
        if (buttons && buttons.length) {
            buttons.forEach(function (btn) {
                const isActive = btn.getAttribute('data-layout') === layout;
                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        }
    }

    getLayoutButtons() {
        const holders = document.querySelectorAll('[data-layout-switcher]');
        const buttons = [];
        holders.forEach(function (holder) {
            holder.querySelectorAll('.collection__nav-layout-btn').forEach(function (btn) {
                buttons.push(btn);
            });
        });
        return buttons;
    }

    onLayoutSwitcherClick(event) {
        const holder = event.target.closest('[data-layout-switcher]');
        if (!holder) return;
        const btn = event.target.closest('.collection__nav-layout-btn');
        if (!btn) return;
        const layout = btn.getAttribute('data-layout');
        if (!layout || this.validLayouts.indexOf(layout) === -1) return;
        const grid = this.querySelector('#collection__main-grid');
        if (!grid) return;
        event.preventDefault();
        this.applyLayout(layout, grid, this.getLayoutButtons());
        localStorage.setItem(this.layoutKey, layout);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('view', layout);
        window.history.replaceState({}, '', currentUrl.toString());
        this.scheduleEditorialPromoHeightUpdate();
    }

    setupEditorialPromoHeight() {
        if (this.promoResizeObserver || typeof ResizeObserver !== 'function') {
            this.scheduleEditorialPromoHeightUpdate();
            return;
        }

        this.promoResizeObserver = new ResizeObserver(() => {
            this.scheduleEditorialPromoHeightUpdate();
        });

        const grid = this.querySelector('#collection__main-grid');
        if (grid) this.promoResizeObserver.observe(grid);
        this.scheduleEditorialPromoHeightUpdate();
    }

    teardownEditorialPromoHeight() {
        if (this._promoHeightRaf) {
            cancelAnimationFrame(this._promoHeightRaf);
            this._promoHeightRaf = null;
        }
        this.promoResizeObserver?.disconnect();
        this.promoResizeObserver = null;
    }

    scheduleEditorialPromoHeightUpdate() {
        if (this._promoHeightRaf) return;
        this._promoHeightRaf = requestAnimationFrame(() => {
            this._promoHeightRaf = null;
            this.updateEditorialPromoHeight();
        });
    }

    updateEditorialPromoHeight() {
        const grid = this.querySelector('#collection__main-grid');
        if (!grid) return;

        const isDesktop = window.matchMedia && window.matchMedia('(min-width: 1025px)').matches;
        const isEditorial = grid.classList.contains('collection__main-grid--editorial');
        const promo = grid.querySelector('.collection__product-cell--promo');
        if (!promo) return;

        if ((promo.dataset.promoType || '').toLowerCase() === 'product') {
            promo.style.setProperty('--promo-info-cut', '0px');
            return;
        }

        if (!isDesktop || !isEditorial) {
            promo.style.removeProperty('--promo-info-cut');
            return;
        }

        // Only the first two rows matter for promo sizing; cap work as the grid grows.
        const productCells = Array.from(
            grid.querySelectorAll('.collection__product-cell--product:not([hidden])')
        ).slice(0, 16);
        if (!productCells.length) return;

        // Determine the 2nd row by grouping items by their rendered top position.
        const rowTops = [];
        const tol = 2;
        productCells.forEach((cell) => {
            if (cell.hasAttribute('hidden')) return;
            const top = Math.round(cell.getBoundingClientRect().top);
            if (!rowTops.some((t) => Math.abs(t - top) <= tol)) rowTops.push(top);
        });
        rowTops.sort((a, b) => a - b);
        const secondRowTop = rowTops[1];
        if (typeof secondRowTop !== 'number') {
            promo.style.setProperty('--promo-info-cut', '0px');
            return;
        }

        const secondRowCells = productCells.filter((cell) => {
            if (cell.hasAttribute('hidden')) return false;
            const top = Math.round(cell.getBoundingClientRect().top);
            return Math.abs(top - secondRowTop) <= tol;
        });
        if (!secondRowCells.length) {
            promo.style.setProperty('--promo-info-cut', '0px');
            return;
        }

        // Measure the tallest `.pc__information` (including its `mt-m` margin-top) in the 2nd row.
        let maxInfoBlock = 0;
        secondRowCells.forEach((cell) => {
            const info = cell.querySelector('.pc__information');
            if (!info) return;
            const cs = window.getComputedStyle(info);
            const mt = parseFloat(cs.marginTop) || 0;
            const h = info.getBoundingClientRect().height || 0;
            const total = Math.max(0, h + mt);
            if (total > maxInfoBlock) maxInfoBlock = total;
        });

        promo.style.setProperty('--promo-info-cut', `${Math.round(maxInfoBlock)}px`);
    }

    setupLayoutSwitcher() {
        const grid = this.querySelector('#collection__main-grid');
        const holders = document.querySelectorAll('[data-layout-switcher]');
        if (!grid || !holders.length) return;
        const buttons = this.getLayoutButtons();
        const layout = this.getLayoutFromUrl() || localStorage.getItem(this.layoutKey) || 'editorial';
        this.applyLayout(layout, grid, buttons);
        const currentUrl = new URL(window.location.href);
        if (!this.getLayoutFromUrl() && layout !== 'editorial') {
            currentUrl.searchParams.set('view', layout);
            window.history.replaceState({}, '', currentUrl.toString());
        }
    }

    onOverlayClick(event) {
        const filterDrawer = document.getElementById('collection__filter-drawer');
        if (!filterDrawer) return;
        if (filterDrawer.getAttribute('aria-expanded') !== 'true') return;

        if (!event.target.closest('.collection__filter-drawer-wrapper')) return;
        if (event.target.closest('.collection__filter-drawer')) return;

        const toggleButton = document.querySelector('[data-action="toggle-drawer"][data-toggle-id="collection__filter-drawer"]');
        if (toggleButton) {
            toggleButton.click();
        }
    }

    /**
     * Load-more only needs this section's markup, but `paginate.next.url` is a
     * full page URL — fetching it downloads the entire document (layout, header,
     * footer, every inline script) just to extract the grid. Ask for the section
     * render instead.
     *
     * @param {string} pageUrl - Paginated URL from data-next-url.
     * @returns {string} The same URL with section_id appended, or the input
     *                   unchanged when the section id cannot be resolved.
     */
    getSectionRenderUrl(pageUrl) {
        const sectionId = this.querySelector('#collection__main-grid')?.dataset.id;
        if (!sectionId) return pageUrl;
        try {
            const url = new URL(pageUrl, window.location.origin);
            url.searchParams.set('section_id', sectionId);
            return url.pathname + url.search;
        } catch (error) {
            return pageUrl;
        }
    }

    getNextPageUrl(paginationEl) {
        const loadMoreWrapper = paginationEl.querySelector('.main-collection__load-more-wrapper');
        return loadMoreWrapper ? loadMoreWrapper.getAttribute('data-next-url') : null;
    }

    setLoadMoreLoadingState(paginationEl, isLoading) {
        if (!paginationEl) return;
        const loadMoreBtn = paginationEl.querySelector('[data-action="load-more"]');
        if (!loadMoreBtn) return;
        const loader = loadMoreBtn.querySelector('.itemLoader');

        loadMoreBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        loadMoreBtn.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        if ('disabled' in loadMoreBtn) {
            loadMoreBtn.disabled = isLoading;
        }
        loader?.classList.toggle('is-visible', Boolean(isLoading));
    }

    announceLoadMoreStatus(paginationEl, message) {
        if (!paginationEl || !message) return;
        const statusEl = paginationEl.querySelector('[data-load-more-status]');
        if (!statusEl) return;
        statusEl.textContent = '';
        requestAnimationFrame(function () {
            statusEl.textContent = message;
        });
    }

    loadNextPage(nextPageUrl) {
        const gridEl = this.querySelector('#collection__main-grid');
        const paginationEl = this.querySelector('#main-collection__pagination');
        if (!nextPageUrl || this.isLoading || !gridEl || !paginationEl) return;

        this.isLoading = true;
        this.setLoadMoreLoadingState(paginationEl, true);
        this.announceLoadMoreStatus(paginationEl, 'Loading more products');
        const prefetchedHtml = this.prefetchedPages.get(nextPageUrl);
        const pagePromise = prefetchedHtml
            ? Promise.resolve(prefetchedHtml)
            : fetch(this.getSectionRenderUrl(nextPageUrl), { credentials: 'same-origin', method: 'GET' }).then(function (response) { return response.text(); });

        pagePromise
            .then(function (html) {
                this.prefetchedPages.delete(nextPageUrl);
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const fetchedGrid = doc.getElementById('collection__main-grid');
                const fetchedPagination = doc.getElementById('main-collection__pagination');
                if (!fetchedGrid) { this.isLoading = false; return; }
                gridEl.insertAdjacentHTML('beforeend', fetchedGrid.innerHTML);
                this.animateNewContent(gridEl);
                if (fetchedPagination) paginationEl.innerHTML = fetchedPagination.innerHTML;
                this.lastLoadTime = Date.now();
                this.isLoading = false;
                this.setLoadMoreLoadingState(paginationEl, false);
                this.announceLoadMoreStatus(paginationEl, 'More products loaded');
                document.dispatchEvent(new CustomEvent('pagination:finished', { bubbles: true }));
            }.bind(this))
            .catch(function () {
                this.isLoading = false;
                this.setLoadMoreLoadingState(paginationEl, false);
                this.announceLoadMoreStatus(paginationEl, 'Unable to load more products');
            }.bind(this));
    }

    onLoadMoreClick(event) {
        const button = event.target.closest('[data-action="load-more"]');
        if (!button || !button.closest('#main-collection__pagination')) return;
        if (button.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        const paginationEl = this.querySelector('#main-collection__pagination');
        if (!paginationEl) return;
        const nextPageUrl = this.getNextPageUrl(paginationEl);
        if (nextPageUrl) this.loadNextPage(nextPageUrl);
    }

    animateNewContent(gridEl) {
        const elementsToReveal = gridEl.querySelectorAll('[data-animation]:not([data-loaded])');
        const animationDuration = (window.theme && window.theme.animationDuration) || 400;
        const animationDelay = (window.theme && window.theme.animationBetweenElements) || 50;
        elementsToReveal.forEach(function (element, index) {
            element.setAttribute('data-loaded', 'true');
            setTimeout(function () {
                element.animate([
                    { transform: 'translateY(30px)', opacity: '0' },
                    { transform: 'translateY(0px)', opacity: '1' }
                ], {
                    duration: animationDuration,
                    fill: 'forwards'
                });
            }, animationDelay * index);
        });
    }

    setup() {
        const gridEl = this.querySelector('#collection__main-grid');
        const paginationEl = this.querySelector('#main-collection__pagination');
        if (!gridEl || !paginationEl) return;
        this.initDelegateBindings();
        this.prefetchedPages.clear();
        this.scheduleIdlePrefetch();
    }

    setupFilterDropdowns() {
        const selector =
            '.collection__filter-drawer .Dropdown[data-filter-dropdown], ' +
            '.collection__filters-inner .Dropdown[data-filter-dropdown]';

        document.querySelectorAll(selector).forEach((dropdown) => {
            dropdown.classList.add('do-not-close');
        });
    }

    onPaginationFinished() {
        const grid = this.querySelector('#collection__main-grid');
        if (!grid) return;
        const productCount = grid.querySelectorAll('.collection__product-cell--product').length;
        grid.querySelectorAll('.collection__product-cell--promo[data-promo-position-mobile]').forEach(function (promo) {
            const posMobile = parseInt(promo.dataset.promoPositionMobile, 10);
            const posDesktop = parseInt(promo.dataset.promoPositionDesktop, 10);
            if (posMobile <= productCount || posDesktop <= productCount) {
                promo.removeAttribute('hidden');
            }
        });
        this.scheduleIdlePrefetch();
        this.scheduleEditorialPromoHeightUpdate();
    }

    cancelPrefetchSchedule() {
        if (this.prefetchIdleHandle && window.cancelIdleCallback) {
            window.cancelIdleCallback(this.prefetchIdleHandle);
            this.prefetchIdleHandle = null;
        }
        if (this.prefetchTimeoutHandle) {
            clearTimeout(this.prefetchTimeoutHandle);
            this.prefetchTimeoutHandle = null;
        }
    }

    prefetchNextPage() {
        if (this.isLoading) return;
        const paginationEl = this.querySelector('#main-collection__pagination');
        if (!paginationEl) return;
        const nextPageUrl = this.getNextPageUrl(paginationEl);
        if (!nextPageUrl || this.prefetchedPages.has(nextPageUrl)) return;

        this.prefetchController?.abort();
        this.prefetchController = new AbortController();
        const signal = this.prefetchController.signal;

        // Cache is left to the server's headers rather than forced off with
        // no-store, so a revalidated response can be reused.
        fetch(this.getSectionRenderUrl(nextPageUrl), { credentials: 'same-origin', method: 'GET', signal: signal })
            .then(function (response) { return response.text(); })
            .then(function (html) {
                if (this.prefetchedPages.size >= this.maxPrefetchCacheSize) {
                    const oldestKey = this.prefetchedPages.keys().next().value;
                    if (oldestKey) this.prefetchedPages.delete(oldestKey);
                }
                this.prefetchedPages.set(nextPageUrl, html);
                if (this.prefetchController?.signal === signal) {
                    this.prefetchController = null;
                }
            }.bind(this))
            .catch(function (error) {
                if (error?.name !== 'AbortError') {
                    // No-op: prefetch should fail silently.
                }
            })
            .finally(function () {
                if (this.prefetchController?.signal === signal) {
                    this.prefetchController = null;
                }
            }.bind(this));
    }

    scheduleIdlePrefetch() {
        this.cancelPrefetchSchedule();
        if (window.requestIdleCallback) {
            this.prefetchIdleHandle = window.requestIdleCallback(function () {
                this.prefetchIdleHandle = null;
                this.prefetchNextPage();
            }.bind(this), { timeout: 1200 });
        } else {
            this.prefetchTimeoutHandle = setTimeout(function () {
                this.prefetchTimeoutHandle = null;
                this.prefetchNextPage();
            }.bind(this), 800);
        }
    }
}

if (!customElements.get('main-collection-grid')) {
    customElements.define('main-collection-grid', MainCollectionGrid);
}